import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./ProductRealization.css";
import realizationImage from "../../../images/about/testing.jpg";

type SolutionItem = { title: string; text: string };
type SolutionSection = {
  title: string;
  paragraphs: string[];
  items?: SolutionItem[];
  listTitle?: string;
};
type FrameworkSection = SolutionSection & {
  frameworkTitle?: string;
  principles?: string[];
};
type SolutionConfig = {
  pageTitle: string;
  variant: "team" | "ownership" | "lifecycle" | "productivity" | "realization";
  hero: { title: string; paragraphs: string[]; cta: string };
  overview: SolutionSection;
  framework: FrameworkSection;
  why: SolutionSection;
  cta: { title: string; paragraphs: string[]; cta: string };
};

const config: SolutionConfig = {
  "pageTitle": "Product Realization & Deployment",
  "hero": {
    "title": "From Silicon Success to Product Success.",
    "paragraphs": [
      "Developing reliable silicon is a significant milestone, but transforming that achievement into a successful product requires continued engineering expertise across validation, system integration, software enablement, hardware platforms, and product engineering. Bridging this transition demands coordinated execution that ensures every component works together as intended in real-world deployment environments.",
      "Digicomm Semiconductor helps customers accelerate product realization by integrating multidisciplinary engineering capabilities that support the journey from validated silicon to deployment-ready products. Through structured engineering, system-level integration, and product-focused execution, we help organizations reduce development risk, improve product readiness, and accelerate the path to market."
    ],
    "cta": "Discuss Your Product Development Goals"
  },
  "overview": {
    "title": "Bridging the Gap Between Silicon and Successful Products",
    "paragraphs": [
      "Completing silicon development is an important milestone, but product success depends on how effectively that silicon is integrated, validated, optimized, and prepared for real-world deployment. Bringing together hardware, software, system integration, and product engineering requires coordinated execution that ensures the final product performs reliably within its intended application.",
      "Digicomm Semiconductor supports this critical transition by combining multidisciplinary engineering capabilities into a structured product realization framework. From silicon validation and hardware enablement to embedded software, system integration, and product engineering, we help customers accelerate product readiness while improving reliability, quality, and deployment confidence."
    ],
    "listTitle": "Ideal Engagement Scenarios",
    "items": [
      {
        "title": "System Integration & Bring-Up",
        "text": "Integrate silicon with hardware platforms, embedded software, and system components to establish a stable and fully functional product foundation."
      },
      {
        "title": "Product Validation & Optimization",
        "text": "Evaluate product performance under real-world operating conditions, optimize system behaviour, and improve overall product reliability."
      },
      {
        "title": "Embedded Software Enablement",
        "text": "Develop and integrate embedded software that enables hardware functionality, system interaction, and application-specific performance."
      },
      {
        "title": "Hardware Platform Readiness",
        "text": "Support board-level validation, platform optimization, and hardware readiness to ensure successful product deployment."
      },
      {
        "title": "Deployment & Lifecycle Readiness",
        "text": "Prepare products for deployment through coordinated engineering activities that improve product quality, operational stability, and long-term maintainability."
      }
    ]
  },
  "framework": {
    "title": "Engineering Products for Real-World Readiness",
    "paragraphs": [
      "Moving from validated silicon to a deployment-ready product requires close coordination across multiple engineering disciplines. Hardware platforms, embedded software, system integration, validation, and product optimization must work together to ensure the product performs reliably in its intended operating environment. Digicomm's Product Realization Framework brings these capabilities together within a structured engineering approach that improves product readiness, minimizes integration challenges, and supports successful deployment.",
      "Our multidisciplinary teams collaborate throughout the realization journey, enabling customers to reduce engineering complexity, accelerate product readiness, and prepare products for reliable deployment with greater confidence."
    ],
    "frameworkTitle": "Our Product Realization Framework",
    "items": [
      {
        "title": "Validate Silicon Performance",
        "text": "Confirm silicon functionality, performance, and operational stability through comprehensive validation activities before product integration."
      },
      {
        "title": "Enable Hardware Platforms",
        "text": "Develop and optimize hardware platforms that establish a reliable foundation for system integration and product development."
      },
      {
        "title": "Integrate Embedded Software",
        "text": "Enable product functionality through embedded software development, platform support, and seamless hardware-software interaction."
      },
      {
        "title": "Perform System Integration",
        "text": "Integrate hardware, software, and system components into a cohesive product while resolving compatibility and performance challenges."
      },
      {
        "title": "Optimize Product Performance",
        "text": "Refine product behaviour through testing, tuning, and engineering optimization to improve reliability, efficiency, and overall user experience."
      },
      {
        "title": "Prepare for Deployment",
        "text": "Complete engineering activities that support deployment readiness, including product validation, documentation, knowledge transfer, and ongoing engineering support."
      }
    ],
    "principles": [
      "System-Level Engineering",
      "Hardware–Software Integration",
      "Deployment-Focused Validation",
      "Cross-Functional Collaboration",
      "Quality Throughout the Lifecycle",
      "Reliable Product Readiness"
    ]
  },
  "why": {
    "title": "Why Organizations Choose Digicomm for Product Realization & Deployment",
    "paragraphs": [
      "Preparing semiconductor products for successful deployment requires more than expertise in individual engineering disciplines. It demands coordinated execution across hardware, software, validation, system integration, and product engineering to ensure every element performs reliably as a complete product. Digicomm brings these capabilities together through a structured engineering approach that helps customers reduce deployment risk, improve product readiness, and accelerate the transition from development to real-world applications."
    ],
    "listTitle": "Our Differentiators",
    "items": [
      {
        "title": "System-Level Engineering Expertise",
        "text": "Our multidisciplinary teams understand how hardware, software, silicon, and system components interact, enabling seamless product realization."
      },
      {
        "title": "Integrated Hardware–Software Enablement",
        "text": "We bring together platform engineering, embedded software, validation, and system integration to create cohesive, deployment-ready products."
      },
      {
        "title": "Deployment-Focused Engineering",
        "text": "Every engineering activity is aligned with the objective of preparing products for reliable operation in real-world environments."
      },
      {
        "title": "Structured Product Validation",
        "text": "Comprehensive validation and optimization activities help improve product quality, performance, reliability, and deployment confidence."
      },
      {
        "title": "Collaborative Engineering Execution",
        "text": "Cross-functional teams work within a unified engineering framework, ensuring continuity, transparency, and efficient coordination throughout product realization."
      },
      {
        "title": "Long-Term Product Engineering Partnership",
        "text": "Beyond deployment, we continue supporting customers through product enhancements, platform evolution, and future engineering initiatives that extend product value over time."
      }
    ]
  },
  "cta": {
    "title": "Bring Your Product to Market with Confidence",
    "paragraphs": [
      "Transforming validated silicon into a successful product requires coordinated engineering, system-level expertise, and disciplined execution across every stage of product realization. Whether you're enabling a new platform, integrating hardware and software, optimizing product performance, or preparing for deployment, Digicomm Semiconductor provides the engineering capabilities to help you accelerate product readiness and deliver with confidence."
    ],
    "cta": "Discuss Your Product Development Goals"
  },
  "variant": "realization"
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TechIcon = ({ index = 0 }: { index?: number }) => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    {index % 5 === 0 && <>
      <rect x="13" y="13" width="22" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="19" y="19" width="10" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 7v6M24 7v6M30 7v6M18 35v6M24 35v6M30 35v6M7 18h6M7 24h6M7 30h6M35 18h6M35 24h6M35 30h6" stroke="currentColor" strokeWidth="1.6"/>
    </>}
    {index % 5 === 1 && <>
      <circle cx="24" cy="17" r="6" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="12" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="36" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M13 39c1-8 5-12 11-12s10 4 11 12M4 37c1-6 4-9 9-9M44 37c-1-6-4-9-9-9" fill="none" stroke="currentColor" strokeWidth="1.7"/>
    </>}
    {index % 5 === 2 && <>
      <path d="M9 34l9-10 8 6 13-17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M32 13h7v7" fill="none" stroke="currentColor" strokeWidth="2"/>
    </>}
    {index % 5 === 3 && <>
      <path d="M24 6l14 6v10c0 10-6 16-14 20C16 38 10 32 10 22V12z" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 24l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
    {index % 5 === 4 && <>
      <circle cx="24" cy="24" r="7" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M24 6v6M24 36v6M6 24h6M36 24h6M11 11l4 4M33 33l4 4M37 11l-4 4M15 33l-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
  </svg>
);

const Paragraphs = ({ items }: { items: string[] }) => (
  <>{items.map((p, i) => <p key={i}>{p}</p>)}</>
);

const HeroVisual = () => (
  <div className="sol-hero-visual realization-hero-artwork">
    <img src={realizationImage} alt="Semiconductor wafer testing and product qualification equipment" />
  </div>
);
const FrameworkVisual = () => {
  const items = config.framework.items || [];
  return (
    <div className={`sol-framework-visual sol-framework-visual--${config.variant}`} aria-hidden="true">
      <div className="sol-framework-center">
        <TechIcon index={config.variant === "team" ? 1 : config.variant === "ownership" ? 3 : config.variant === "productivity" ? 4 : 0}/>
      </div>
      {items.slice(0, 6).map((_, i) => (
        <span key={i} className={`sol-orbit sol-orbit--${i + 1}`}>
          <i>{String(i + 1).padStart(2, "0")}</i>
        </span>
      ))}
    </div>
  );
};

const Page: React.FC = () => (
  <Fragment>
    <div className={`solution-experience solution-experience--${config.variant} inner-page`}>
      <div className="body_wrap o-clip">
        <Header />
        <main>
          <PageTitle pageTitle={config.pageTitle} pagesub={config.pageTitle} />

          <section className="sol-hero">
            <div className="sol-shell sol-hero-layout">
              <div className="sol-copy sol-hero-copy">
                <h1>{config.pageTitle}</h1>
                <h2>{config.hero.title}</h2>
                <Paragraphs items={config.hero.paragraphs}/>
                {config.hero.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.hero.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <HeroVisual />
            </div>
          </section>

          <section className="sol-section sol-overview">
            <div className="sol-shell">
              <div className="sol-heading-layout">
                <div className="sol-copy"><h2>{config.overview.title}</h2></div>
                <div className="sol-copy"><Paragraphs items={config.overview.paragraphs}/></div>
              </div>
              {config.overview.listTitle && <h3 className="sol-list-heading">{config.overview.listTitle}</h3>}
              <div className="sol-scenario-grid">
                {(config.overview.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span className="sol-index">{String(i + 1).padStart(2, "0")}</span>
                    <div className="sol-icon"><TechIcon index={i}/></div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="sol-section sol-framework">
            <div className="sol-shell">
              <div className="sol-framework-top">
                <div className="sol-copy">
                  <h2>{config.framework.title}</h2>
                  <Paragraphs items={config.framework.paragraphs}/>
                </div>
                <FrameworkVisual />
              </div>

              {config.framework.frameworkTitle && <h3 className="sol-list-heading">{config.framework.frameworkTitle}</h3>}

              <div className="sol-framework-steps">
                {(config.framework.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <div><h3>{item.title}</h3><p>{item.text}</p></div>
                  </article>
                ))}
              </div>

              {!!config.framework.principles?.length && (
                <div className="sol-principles">
                  {config.framework.principles.map((x) => <span key={x}>{x}</span>)}
                </div>
              )}
            </div>
          </section>

          <section className="sol-section sol-why">
            <div className="sol-shell sol-why-layout">
              <div className="sol-copy">
                <h2>{config.why.title}</h2>
                <Paragraphs items={config.why.paragraphs}/>
              </div>

              <div className={`sol-value-system sol-value-system--${config.variant}`}>
                <div className="sol-value-center">
                  <TechIcon index={config.variant === "productivity" ? 4 : config.variant === "team" ? 1 : 0}/>
                  <strong>{config.pageTitle}</strong>
                </div>
                <div className="sol-value-grid">
                  {(config.why.items || []).map((item, i) => (
                    <article key={item.title}>
                      <div className="sol-icon"><TechIcon index={i}/></div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="sol-cta">
            <div className="sol-shell sol-cta-layout">
              <div className="sol-copy">
                <h2>{config.cta.title}</h2>
                <Paragraphs items={config.cta.paragraphs}/>
                {config.cta.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.cta.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <div className="sol-cta-visual" aria-hidden="true">
                <div className="sol-globe"><i/><i/><i/><i/><i/></div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <Scrollbar />
      </div>
    </div>
  </Fragment>
);

export default Page;
